// src/encounter/prescription-cards.tsx
//
// PRESCRIPTION CARDS (`?x=prescription-card`, src/shared/lib/encounter-flags.ts) — Antonin's catalog PrescriptionCard
// (scrips-signal-ds @166c3c35, catalog/src/components/PrescriptionCard; placed by EncounterDev personaBlocks `code:ap`, "Changed vs
// dev (2 Nov): medication orders as prescription cards (dev's prescription form opens from Add prescription); the other order types
// stay in Add order"). A "Prescriptions" title, one grey inner card per drug (drug, then its schedule in meta; edit and remove on the
// right), then Add prescription ("Add another prescription" once there is one).
//
// Display only. The cards are the plan's medication orders as the host holds them; edit, remove and add call the A/P view's own
// handlers (today's dose dialog, onRemoveOrder, today's medication search), so the payloads and the save path are unchanged.
// PA extras the catalog card does not draw (kept so no data is removed, listed for Antonin): the drug code after the name, the
// priority chip and drug-interaction glyph, the sig / refills / dispense line and the linked diagnosis codes.
import type { ReactNode } from 'react';
import { Button } from '@scripsteam/scrips-signal-ds';
import { Pencil, Plus, X } from 'lucide-react';

export interface PrescriptionCardItem {
  id: string;
  /** The order's name as saved (PA keeps the strength in it; there is no separate strength field). */
  name: string;
  code?: string;
  /** Frequency · times a day · units per intake · route, from the saved dose. */
  schedule?: string;
  /** Sig · refills · dispense: the line the order row showed. */
  detail?: string;
  dxCodes?: readonly string[];
  /** Priority chip and interaction glyph, as the order row drew them. */
  signals?: ReactNode;
}

export interface PrescriptionCardsProps {
  items: readonly PrescriptionCardItem[];
  onEdit?: (id: string) => void;
  onRemove?: (id: string) => void;
  /** Absent: no Add prescription (read-only, or no medication ordering for this user). */
  onAdd?: () => void;
  /** Set: Add prescription shows but is disabled, with this as its title (PA's form needs a diagnosis). */
  addDisabledReason?: string;
}

export function PrescriptionCards({ items, onEdit, onRemove, onAdd, addDisabledReason }: PrescriptionCardsProps) {
  // Nothing to show and nothing to do (a signed note, or a user who cannot order medications): no block, not an empty state.
  if (items.length === 0 && !onAdd && !addDisabledReason) return null;
  return (
    <section data-slot="ap-prescriptions" aria-label="Prescriptions" className="flex min-w-0 flex-col gap-2">
      <h4 className="m-0 text-xs font-medium text-text-primary">Prescriptions</h4>
      {items.length === 0 && <p className="m-0 text-sm text-text-secondary">No prescriptions yet.</p>}
      {items.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {items.map((p) => {
            const meta = [p.detail, p.dxCodes?.length ? p.dxCodes.join(', ') : undefined].filter(Boolean).join(' · ');
            return (
              <li
                key={p.id}
                data-slot="ap-prescription-card"
                className="flex items-center gap-3 rounded-md bg-surface-panel py-2 pr-2 pl-3"
              >
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="text-sm font-medium text-text-primary" dir="auto">
                      {p.name}
                      {p.code ? <span className="font-normal text-text-secondary" dir="ltr"> {p.code}</span> : null}
                    </span>
                    {p.signals}
                  </span>
                  {p.schedule ? <span className="text-xs text-text-muted">{p.schedule}</span> : null}
                  {meta ? <span className="text-xs text-text-muted">{meta}</span> : null}
                </span>
                {(onEdit || onRemove) && (
                  <span className="flex flex-none gap-1">
                    {onEdit && (
                      <Button
                        variant="tertiary"
                        className="text-text-secondary"
                        size="sm"
                        icon={<Pencil className="size-4" aria-hidden />}
                        aria-label={`Edit ${p.name}`}
                        onClick={() => onEdit(p.id)}
                      />
                    )}
                    {onRemove && (
                      <Button
                        variant="tertiary"
                        className="text-text-secondary"
                        size="sm"
                        icon={<X className="size-4" aria-hidden />}
                        aria-label={`Remove ${p.name}`}
                        onClick={() => onRemove(p.id)}
                      />
                    )}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {(onAdd || addDisabledReason) && (
        <span className="self-start">
          <Button
            variant="tertiary"
            className="text-text-secondary"
            size="sm"
            icon={<Plus className="size-4" aria-hidden />}
            disabled={!onAdd}
            title={onAdd ? undefined : addDisabledReason}
            onClick={onAdd}
          >
            {items.length ? 'Add another prescription' : 'Add prescription'}
          </Button>
        </span>
      )}
    </section>
  );
}
